/**
 * Off-Page Brief workspace — 2-column variant editor (spec §5.7.3).
 *
 * Layout:
 *   Left rail (~30%): variant list grouped by platform.
 *   Right pane:       Content | Prompt tab (segmented control) + action row.
 *
 * State ownership:
 *   - Selected variant + selected tab live locally here (the URL doesn't
 *     encode which variant is open — the workspace is a single unit).
 *   - Body edits are held in a local draft until the tenant clicks Save,
 *     at which point `usePatchVariant` fires. We use an explicit Save
 *     button (not blur-save) so the tenant knows exactly when persistence
 *     happens and can freely discard drafts by picking another variant.
 *   - Hook A/B toggle patches immediately (single-write, no draft state).
 *   - Post-anyway override for `needs_review` variants lives per-variant
 *     in a Set — once flipped it persists until the workspace unmounts.
 *
 * Corner cases handled:
 *   - Detail loading / error / empty variants
 *   - Off-page brief still generating (auto-refetch every 5s, editing disabled)
 *   - Off-page brief `failed` (retry via regenerate)
 *   - Variant `posted` → read-only + tooltips
 *   - Variant `needs_review` → Copy greyed with tooltip + "Post anyway" opt-in
 *   - Autopostable variants → Publish button + confirmation modal
 *   - Publish 501 `AUTOPOST_NOT_YET_AVAILABLE` → informative toast
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AlertOctagon,
  Check,
  Copy,
  ExternalLink,
  FileDown,
  Info,
  Loader2,
  RefreshCw,
  Send,
  Sparkles,
  X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import type { OffPageBriefDetail, OffPageBriefVariant } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Surface } from '@/components/ui/surface';
import { Callout } from '@/components/ui/callout';
import { EmptyState } from '@/components/ui/empty';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ApiError, summarizeApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { downloadAuthed } from '@/lib/download';
import { useAuthStore } from '@/lib/auth';
import { relTime } from '@/lib/format';
import { usePatchVariant } from '@/features/off-page-briefs/use-patch-variant';
import { useRegenerateOffPageBrief } from '@/features/off-page-briefs/use-regenerate-off-page-brief';
import {
  PLATFORM_LABELS,
  PLATFORM_ORDER,
  platformComposeUrl,
  qualityBadgeVariant,
  variantRowLabel,
} from '@/features/off-page-briefs/lib/variants';
import { apiPost } from '@/lib/api';
import { MarkPostedModal } from './mark-posted-modal';
import { PublishConfirmModal } from './publish-confirm-modal';

interface WorkspaceProps {
  detail: OffPageBriefDetail;
  /** Set of platform ids the tenant asked to re-generate individually. When
   *  non-empty, the route suppresses the full-workflow progress card and this
   *  workspace shows an inline spinner on just the affected variant rows. */
  partialRegenPlatforms?: Set<string>;
  /** Called before the regenerate mutation fires so the route can start
   *  tracking a partial regen and drop the full progress card. */
  onStartPartialRegen?: (platform: string) => void;
  /** Cancel an in-flight partial regen. Route calls the OPB cancel endpoint
   *  which stops the underlying Temporal workflow; existing variants stay put. */
  onCancelPartialRegen?: () => void;
}

/**
 * Read a target field with a soft type. The wire schema types `target` as
 * `unknown` because its shape varies per platform; we only surface a few
 * common keys in the info strip.
 */
interface VariantTarget {
  subreddit?: string;
  question_url?: string;
  thread_url?: string;
  hashtags?: string[];
}

function readTarget(v: OffPageBriefVariant): VariantTarget {
  return (v.target && typeof v.target === 'object' ? v.target : {}) as VariantTarget;
}

interface CritiqueNote {
  severity: 'info' | 'warn' | 'block';
  category?: string;
  message: string;
  location?: string;
}

function readCritiqueNotes(v: OffPageBriefVariant): CritiqueNote[] {
  if (!Array.isArray(v.critique_notes)) return [];
  return (v.critique_notes as unknown[]).filter((n): n is CritiqueNote => {
    if (!n || typeof n !== 'object') return false;
    const rec = n as Record<string, unknown>;
    return (
      typeof rec.severity === 'string' &&
      typeof rec.message === 'string' &&
      ['info', 'warn', 'block'].includes(rec.severity)
    );
  });
}

export function OffPageBriefWorkspace({
  detail,
  partialRegenPlatforms,
  onStartPartialRegen,
  onCancelPartialRegen,
}: WorkspaceProps): JSX.Element {
  // First variant selected on mount; kept as an id so cache invalidations
  // that produce new variant objects (same id, refreshed body) still
  // resolve correctly.
  const [selectedId, setSelectedId] = useState<string>(
    detail.variants[0]?.id ?? '',
  );
  const [tab, setTab] = useState<'content' | 'prompt'>('content');
  const [markPostedOpen, setMarkPostedOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Draft body per variant — cleared when the tenant switches away without
  // saving (rare) or after a successful save. Held keyed by variant id so
  // switching between two variants doesn't cross-contaminate drafts.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [exportingPdf, setExportingPdf] = useState(false);
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useAuthStore((s) => s.tenant);

  // Track what the tenant was last looking at BEFORE regenerate replaces the
  // row. When the delete-then-insert cycle produces a fresh id for the same
  // (platform, hook_variant) pair, we re-select it so focus doesn't jump to
  // an arbitrary "first variant" — a regen jumping the tenant from
  // linkedin_post/A to reddit/A is disorienting.
  const lastSelectionRef = useRef<{ platform: string; hook: string } | null>(
    null,
  );
  useEffect(() => {
    const s = detail.variants.find((v) => v.id === selectedId);
    if (s) {
      lastSelectionRef.current = { platform: s.platform, hook: s.hook_variant };
    }
  }, [detail.variants, selectedId]);

  useEffect(() => {
    if (!selectedId || detail.variants.some((v) => v.id === selectedId)) return;
    // Selected id disappeared (regen just completed). Try to re-anchor onto
    // the same platform + hook first; only fall back to the first variant if
    // that pair no longer exists.
    const last = lastSelectionRef.current;
    const sameCombo =
      last &&
      detail.variants.find(
        (v) => v.platform === last.platform && v.hook_variant === last.hook,
      );
    const samePlatform =
      last && detail.variants.find((v) => v.platform === last.platform);
    const next = sameCombo ?? samePlatform ?? detail.variants[0];
    if (next) setSelectedId(next.id);
  }, [detail.variants, selectedId]);

  const selected = detail.variants.find((v) => v.id === selectedId) ?? detail.variants[0];

  const patch = usePatchVariant(detail.id);
  const regenerate = useRegenerateOffPageBrief(detail.id);

  const grouped = useMemo(() => {
    const map = new Map<string, OffPageBriefVariant[]>();
    for (const v of detail.variants) {
      const list = map.get(v.platform) ?? [];
      list.push(v);
      map.set(v.platform, list);
    }
    return PLATFORM_ORDER.filter((p) => map.has(p)).map((p) => ({
      platform: p,
      variants: map.get(p) ?? [],
    }));
  }, [detail.variants]);

  // The selected variant may be mid-regen. Its persisted body is still on
  // screen (deliberate — see "preserve old body" story) but edits would be
  // wiped when the new version lands, so the textarea flips to read-only.
  const selectedIsRegenerating =
    selected != null &&
    partialRegenPlatforms?.has(selected.platform) === true;
  const isEditable =
    detail.status === 'ready' &&
    selected &&
    selected.status !== 'posted' &&
    !selectedIsRegenerating;

  const draftBody = selected ? drafts[selected.id] : undefined;
  const currentBody = draftBody ?? selected?.body ?? '';
  const isDirty = draftBody !== undefined && draftBody !== selected?.body;

  const save = (): void => {
    if (!selected || !isDirty) return;
    patch.mutate(
      { variantId: selected.id, body: { body: currentBody } },
      {
        onSuccess: () => {
          setDrafts((d) => {
            const next = { ...d };
            delete next[selected.id];
            return next;
          });
          toast.success('Saved');
        },
        onError: (e) => {
          const { title, description } = summarizeApiError(e, {
            fallbackTitle: 'Could not save',
          });
          toast.error(title, { description });
        },
      },
    );
  };

  // Hook A/B are separate variant rows sharing (platform, mode). Switching
  // hooks means selecting the sibling row — not patching the current one.
  const sibling = useMemo(() => {
    if (!selected) return undefined;
    return detail.variants.find(
      (v) =>
        v.id !== selected.id &&
        v.platform === selected.platform &&
        v.mode === selected.mode,
    );
  }, [detail.variants, selected]);

  const setHook = (targetLetter: 'a' | 'b'): void => {
    if (!selected) return;
    const currentLetter =
      selected.hook_variant?.toLowerCase() === 'b' ? 'b' : 'a';
    if (currentLetter === targetLetter) return;
    if (sibling) {
      setSelectedId(sibling.id);
    }
  };

  const copyContent = async (v: OffPageBriefVariant): Promise<void> => {
    // The persisted `body` already embodies whichever hook is selected on
    // the row (hook A/B are separate variant rows, sharing platform+mode).
    // So we copy the body verbatim — no need to prepend the hook string.
    const bodyForCopy = v.id === selected?.id ? currentBody : v.body;
    try {
      await navigator.clipboard.writeText(bodyForCopy);
      toast.success('Copied to clipboard', {
        description: 'Opening the compose page in a new tab.',
      });
    } catch {
      toast.error('Copy failed', {
        description: 'Your browser blocked clipboard access.',
      });
      return;
    }
    window.open(platformComposeUrl(v), '_blank', 'noopener,noreferrer');
  };

  const exportPdf = async (v: OffPageBriefVariant): Promise<void> => {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      await downloadAuthed({
        url: `/v1/off-page-briefs/${detail.id}/variants/${v.id}/report.pdf`,
        filename: `off-page-${v.platform}-${v.id.slice(0, 8)}.pdf`,
        accessToken,
        tenantId: tenant?.id,
      });
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error('Export failed', {
        description: e instanceof Error ? e.message : 'Could not render the PDF.',
      });
    } finally {
      setExportingPdf(false);
    }
  };

  const copyPrompt = async (v: OffPageBriefVariant): Promise<void> => {
    try {
      await navigator.clipboard.writeText(v.generation_prompt);
      toast.success('Prompt copied');
    } catch {
      toast.error('Copy failed', {
        description: 'Your browser blocked clipboard access.',
      });
    }
  };

  const regenerateVariant = (v: OffPageBriefVariant): void => {
    // Warn before regen wipes an unsaved body draft — silent data loss on a
    // destructive action is one of the loudest UX complaints.
    if (isDirty && v.id === selected?.id) {
      const ok = window.confirm(
        'You have unsaved changes to this variant. Regenerate will discard them and start over. Continue?',
      );
      if (!ok) return;
      // Purge the draft locally so the workspace snaps back to the persisted
      // body while regen runs — otherwise we'd render the doomed draft next
      // to a "Regenerating…" pill, which is worse than either state alone.
      setDrafts((d) => {
        const next = { ...d };
        delete next[v.id];
        return next;
      });
    }
    // Tell the route this is a per-variant regen so it doesn't switch to the
    // full "Writing your variants" progress card and freeze the whole page.
    onStartPartialRegen?.(v.platform);
    regenerate.mutate(
      { platforms: [v.platform] },
      {
        onSuccess: () => {
          toast.success('Regenerating', {
            description: `The ${PLATFORM_LABELS[v.platform]} variant will refresh in a moment.`,
          });
        },
        onError: (e) => {
          if (
            e instanceof ApiError &&
            e.code === 'OFF_PAGE_BRIEF_ALREADY_REGENERATING'
          ) {
            toast.info('Already regenerating', {
              description: 'A regenerate run is already in flight — hang tight.',
            });
            return;
          }
          const { title, description } = summarizeApiError(e, {
            fallbackTitle: 'Could not regenerate',
          });
          toast.error(title, { description });
        },
      },
    );
  };

  const publish = (v: OffPageBriefVariant): void => {
    setPublishing(true);
    void (async () => {
      try {
        await apiPost<{ status: string }>(
          `/v1/off-page-briefs/${detail.id}/variants/${v.id}/publish`,
          {},
        );
        toast.success('Publishing', {
          description: 'Your variant is being published — this may take a moment.',
        });
        setPublishOpen(false);
      } catch (e) {
        if (
          e instanceof ApiError &&
          e.code === 'AUTOPOST_NOT_YET_AVAILABLE'
        ) {
          toast.info('Autopost lands in Phase 3', {
            description: 'Manual posting works today — use Copy or Mark as posted.',
          });
          setPublishOpen(false);
          return;
        }
        const { title, description } = summarizeApiError(e, {
          fallbackTitle: 'Could not publish',
        });
        toast.error(title, { description });
      } finally {
        setPublishing(false);
      }
    })();
  };

  // Guard: detail is `ready` but no variants exist. Shouldn't happen in
  // practice (planning would fail first) but we render a helpful empty
  // state so the UI doesn't leave the tenant confused.
  if (detail.variants.length === 0) {
    return (
      <EmptyState
        icon={Sparkles}
        title="No variants generated yet"
        description="Regenerate this off-page brief to try again — the previous run produced no variants."
        action={
          <Button
            onClick={() =>
              regenerate.mutate(
                {},
                {
                  onSuccess: () => toast.success('Regenerating'),
                  onError: (e) => {
                    const { title, description } = summarizeApiError(e, {
                      fallbackTitle: 'Could not regenerate',
                    });
                    toast.error(title, { description });
                  },
                },
              )
            }
            disabled={regenerate.isPending}
          >
            {regenerate.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Regenerating…
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" /> Regenerate
              </>
            )}
          </Button>
        }
      />
    );
  }

  if (!selected) {
    return (
      <EmptyState
        title="Pick a variant"
        description="Select a variant from the left rail to start editing."
      />
    );
  }

  const critiqueNotes = readCritiqueNotes(selected);
  const target = readTarget(selected);
  const hookLetter =
    selected.hook_variant?.toLowerCase() === 'b' ? 'b' : 'a';
  // needs_review used to gate the Copy button behind a "Post anyway →" link,
  // which felt like a punishment for content that was actually fine. Now
  // Copy is always available as long as the variant is editable — the
  // critique row above the body warns the tenant to review; we trust them
  // to decide.
  const canCopy = isEditable;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        {/* ─────────── Left rail ─────────── */}
        <Surface tone="muted" className="max-h-[calc(100vh-14rem)] overflow-y-auto p-2">
          <div className="space-y-4">
            {grouped.map((group) => (
              <div key={group.platform} className="space-y-1">
                <div className="px-2 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {PLATFORM_LABELS[group.platform]}
                </div>
                {group.variants.map((v) => {
                  const isRegen =
                    partialRegenPlatforms?.has(v.platform) === true;
                  return (
                    <VariantRow
                      key={v.id}
                      variant={v}
                      active={v.id === selected.id}
                      regenerating={isRegen}
                      onSelect={() => setSelectedId(v.id)}
                      onCancelRegen={
                        isRegen ? onCancelPartialRegen : undefined
                      }
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </Surface>

        {/* ─────────── Right pane ─────────── */}
        <div className="space-y-4">
          <TabBar
            tab={tab}
            onChange={setTab}
            variantLabel={variantRowLabel(selected)}
            onExportPdf={() => void exportPdf(selected)}
            exporting={exportingPdf}
          />

          {tab === 'content' ? (
            <ContentTab
              variant={selected}
              sibling={sibling}
              currentBody={currentBody}
              isDirty={isDirty}
              isEditable={!!isEditable}
              saving={patch.isPending}
              hookLetter={hookLetter}
              target={target}
              critiqueNotes={critiqueNotes}
              canCopy={canCopy}
              onBodyChange={(next) =>
                setDrafts((d) => ({ ...d, [selected.id]: next }))
              }
              onSave={save}
              onSetHook={setHook}
              onCopy={() => void copyContent(selected)}
              onMarkPosted={() => setMarkPostedOpen(true)}
              onRegenerate={() => regenerateVariant(selected)}
              onPublish={() => setPublishOpen(true)}
              regenPending={
                regenerate.isPending ||
                partialRegenPlatforms?.has(selected.platform) === true
              }
              regenerating={selectedIsRegenerating}
            />
          ) : (
            <PromptTab variant={selected} onCopyPrompt={() => void copyPrompt(selected)} />
          )}
        </div>
      </div>

      <MarkPostedModal
        opbId={detail.id}
        variant={selected}
        open={markPostedOpen}
        onOpenChange={setMarkPostedOpen}
      />
      <PublishConfirmModal
        variant={selected}
        open={publishOpen}
        onOpenChange={setPublishOpen}
        onConfirm={() => publish(selected)}
        loading={publishing}
      />
    </TooltipProvider>
  );
}

/* ─────────────────────────── Variant list row ─────────────────────────── */

function VariantRow({
  variant,
  active,
  regenerating,
  onSelect,
  onCancelRegen,
}: {
  variant: OffPageBriefVariant;
  active: boolean;
  /** True while a partial regen for this variant's platform is in flight.
   *  Row shows a spinner in place of the status dot + "Regenerating" text
   *  instead of the score badge. Row stays clickable so the tenant can still
   *  navigate to it and see the old version underneath the pending overlay. */
  regenerating: boolean;
  onSelect: () => void;
  /** Only wired for the currently-regenerating rows; clicking cancels the
   *  underlying Temporal workflow so the old variants stay put. */
  onCancelRegen?: () => void;
}): JSX.Element {
  const badge = qualityBadgeVariant(variant.critique_score);
  const hookLetter =
    variant.hook_variant?.toLowerCase() === 'b' ? 'B' : 'A';
  const statusTone =
    variant.status === 'posted'
      ? 'bg-success'
      : variant.status === 'needs_review'
        ? 'bg-warning'
        : variant.status === 'failed' || variant.status === 'removed'
          ? 'bg-destructive'
          : 'bg-muted-foreground/50';
  const statusLabel =
    variant.status === 'needs_review' ? 'Needs review' : variant.status;
  return (
    <button
      type="button"
      onClick={onSelect}
      title={
        regenerating
          ? `${variantRowLabel(variant)} · regenerating…`
          : `${variantRowLabel(variant)} · ${statusLabel} · score ${variant.critique_score}`
      }
      className={cn(
        'group flex w-full items-center gap-2.5 rounded-md border px-2.5 py-1.5 text-left transition-colors',
        active
          ? 'border-primary/40 bg-primary/10'
          : 'border-transparent hover:border-border/60 hover:bg-background/60',
        regenerating && 'border-primary/25 bg-primary/5',
      )}
    >
      {/* Status indicator — spinner while a partial regen is in flight,
          otherwise a colored dot encoding the variant's status. */}
      {regenerating ? (
        <Loader2 className="h-3 w-3 flex-none animate-spin text-primary" />
      ) : (
        <span
          className={cn('h-1.5 w-1.5 flex-none rounded-full', statusTone)}
          aria-label={statusLabel}
        />
      )}
      {/* Row label, truncates on overflow. */}
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
        {variantRowLabel(variant)}
      </span>
      {/* Right-most cell: score badge OR live "Regenerating" pill with an
          inline X to cancel just this platform's regen. */}
      {regenerating ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 pl-1.5 pr-0.5 py-0 text-[10px] font-medium text-primary">
          Regenerating
          {onCancelRegen ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCancelRegen();
              }}
              className="ml-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full text-primary/70 transition-colors hover:bg-primary/25 hover:text-primary"
              aria-label="Cancel regenerate"
              title="Cancel regenerate"
            >
              <XIcon className="h-2.5 w-2.5" />
            </button>
          ) : null}
        </span>
      ) : (
        <Badge variant={badge} className="px-1.5 py-0 text-[10px]">
          {variant.critique_score}
        </Badge>
      )}
      {/* Hook letter chip — always visible so the tenant can still tell A
          from B while the regen is running. */}
      <span
        className={cn(
          'flex h-5 w-5 flex-none items-center justify-center rounded-full border text-[10px] font-semibold',
          active
            ? 'border-primary/60 bg-primary/15 text-primary'
            : 'border-border/60 bg-background text-muted-foreground',
        )}
        aria-label={`Hook ${hookLetter} selected`}
      >
        {hookLetter}
      </span>
    </button>
  );
}

/* ─────────────────────────── Tab bar ─────────────────────────── */

function TabBar({
  tab,
  onChange,
  variantLabel,
  onExportPdf,
  exporting,
}: {
  tab: 'content' | 'prompt';
  onChange: (t: 'content' | 'prompt') => void;
  variantLabel: string;
  onExportPdf?: () => void;
  exporting?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2">
      <div className="text-sm font-medium text-foreground">{variantLabel}</div>
      <div className="flex items-center gap-2">
        {onExportPdf ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onExportPdf}
            disabled={exporting}
            title="Download this variant as a PDF (content + prompt + posting playbook)"
          >
            {exporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileDown className="h-3.5 w-3.5" />
            )}
            Export PDF
          </Button>
        ) : null}
        <div
          role="tablist"
          className="inline-flex items-center rounded-md border border-border/70 bg-muted/40 p-0.5"
        >
          <TabButton active={tab === 'content'} onClick={() => onChange('content')}>
            Content
          </TabButton>
          <TabButton active={tab === 'prompt'} onClick={() => onChange('prompt')}>
            Prompt
          </TabButton>
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'rounded-sm px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────── Content tab ─────────────────────────── */

interface ContentTabProps {
  variant: OffPageBriefVariant;
  /** Sibling variant with the other hook letter (same platform + mode). */
  sibling: OffPageBriefVariant | undefined;
  currentBody: string;
  isDirty: boolean;
  isEditable: boolean;
  saving: boolean;
  hookLetter: 'a' | 'b';
  target: VariantTarget;
  critiqueNotes: CritiqueNote[];
  canCopy: boolean;
  onBodyChange: (next: string) => void;
  onSave: () => void;
  onSetHook: (letter: 'a' | 'b') => void;
  onCopy: () => void;
  onMarkPosted: () => void;
  onRegenerate: () => void;
  onPublish: () => void;
  regenPending: boolean;
  /** True while a partial regen is in flight for this variant's platform.
   *  Content tab renders a "new draft incoming" banner + freezes the body
   *  so the tenant's edits don't get wiped when the new version lands. */
  regenerating: boolean;
}

function ContentTab({
  variant,
  sibling,
  currentBody,
  isDirty,
  isEditable,
  saving,
  hookLetter,
  target,
  critiqueNotes,
  canCopy,
  onBodyChange,
  onSave,
  onSetHook,
  onCopy,
  onMarkPosted,
  onRegenerate,
  onPublish,
  regenPending,
  regenerating,
}: ContentTabProps): JSX.Element {
  // Resolve the "A" and "B" hook previews from the current variant + its
  // sibling. Each variant row is already tagged with one letter — we mirror
  // the sibling into the other slot so the toggle can show both previews.
  const currentLetter: 'a' | 'b' = hookLetter;
  const hookPreviews: Record<'a' | 'b', string | undefined> = {
    a: currentLetter === 'a' ? variant.body : sibling?.body,
    b: currentLetter === 'b' ? variant.body : sibling?.body,
  };
  const posted = variant.status === 'posted';
  const needsReview = variant.status === 'needs_review';
  const targetSummary =
    target.subreddit
      ? `r/${target.subreddit}`
      : target.question_url
        ? new URL(target.question_url).hostname
        : target.thread_url
          ? new URL(target.thread_url).hostname
          : target.hashtags && target.hashtags.length > 0
            ? target.hashtags.slice(0, 3).map((h) => `#${h}`).join(' ')
            : '—';

  // Plain-language placement — "in the comments" is what a tenant would
  // actually say to someone; "comment" alone is jargon.
  const placementLabel =
    variant.link_placement === 'comment'
      ? 'link in a reply'
      : variant.link_placement === 'body'
        ? 'link in the body'
        : variant.link_placement === 'bio_only'
          ? 'link in bio'
          : 'no link';
  const linkTypeLabel = variant.is_expected_nofollow
    ? "won't pass SEO juice"
    : 'passes SEO juice';

  return (
    <div className="flex h-full flex-col gap-3">
      {/* Meta strip — plain-language so it reads like a caption, not jargon. */}
      <div className="flex flex-wrap items-center gap-1.5">
        <MetaChip label="Post to" value={targetSummary} />
        <MetaChip label="Placement" value={placementLabel} />
        <MetaChip label="Link type" value={linkTypeLabel} />
      </div>

      {posted && variant.posted_url ? (
        <Callout
          tone="success"
          title="Already posted"
          action={
            <Button asChild variant="outline" size="sm">
              <a
                href={variant.posted_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-3.5 w-3.5" /> View
              </a>
            </Button>
          }
        >
          Edits to this variant won't be saved. Regenerate to start a new draft.
        </Callout>
      ) : null}

      {/* Hook A/B toggle — segmented pill row instead of two full cards.
          Keeps vertical space for the body editor. Full text of the active
          hook is redundant with the body it produced (hook is the body's
          opening line), so preview is just a single short line. */}
      {sibling ? (
        <div className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 p-1">
          <HookPillOption
            letter="a"
            text={hookPreviews.a ?? ''}
            active={currentLetter === 'a'}
            onSelect={() => onSetHook('a')}
          />
          <HookPillOption
            letter="b"
            text={hookPreviews.b ?? ''}
            active={currentLetter === 'b'}
            onSelect={() => onSetHook('b')}
          />
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          Hook {currentLetter.toUpperCase()} · only one hook was generated for this variant.
        </div>
      )}

      {/* Follow-up comment card — for platforms where the link belongs in a
          reply (Reddit's community norm). Without this, the tenant only sees
          the body (which has no link) and thinks the URL was dropped. */}
      {variant.link_placement === 'comment' && variant.canonical_url ? (
        <FollowUpCommentCard canonicalUrl={variant.canonical_url} />
      ) : null}

      {/* Regen-in-flight banner — tells the tenant the body they're seeing is
          the OLD version and about to be replaced, so their draft edits will
          be lost. The textarea is already read-only via isEditable=false. */}
      {regenerating ? (
        <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
          <Loader2 className="h-3.5 w-3.5 flex-none animate-spin text-primary" />
          <span className="min-w-0 flex-1 text-foreground">
            A new version of this variant is being written. Your current
            content stays here until it lands.
          </span>
        </div>
      ) : null}

      {/* Body editor — flex-grow so it fills the pane instead of leaving
          whitespace at the bottom. Save button lives on the header row
          right — always visible, doesn't collide with actions. */}
      <div className="flex min-h-0 flex-1 flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Body
          </div>
          <div className="flex items-center gap-2 text-xs">
            {isDirty ? (
              <span className="text-warning">Unsaved changes</span>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={onSave}
              disabled={!isDirty || saving || !isEditable}
            >
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <Check className="h-3.5 w-3.5" /> Save
                </>
              )}
            </Button>
          </div>
        </div>
        <textarea
          className={cn(
            'w-full flex-1 rounded-md border border-input bg-background p-3 font-mono text-sm leading-relaxed text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'min-h-[420px]',
            !isEditable && 'cursor-not-allowed opacity-70',
          )}
          value={currentBody}
          onChange={(e) => onBodyChange(e.target.value)}
          disabled={!isEditable}
          readOnly={!isEditable}
          spellCheck
        />
      </div>

      {/* Critique notes — compact collapsible so it doesn't push the
          action row below the fold. Only the first note is shown by default;
          "Show N more" expands the rest. When the notes signal a fallback
          (the "could not automatically score" message the workflow inserts
          on plan-variants failure), we render an inline Try again button
          instead of asking the tenant to hunt for the Regen button. */}
      {needsReview && critiqueNotes.length > 0 ? (
        <CritiqueNotesRow
          notes={critiqueNotes}
          isRegenFallback={isRegenFallback(critiqueNotes)}
          onRetry={onRegenerate}
          retrying={regenPending}
        />
      ) : null}

      {/* Action row */}
      <div className="flex flex-wrap items-center gap-2 border-t border-border/60 pt-4">
        {canCopy ? (
          <Button onClick={onCopy} disabled={posted}>
            <Copy className="h-4 w-4" /> Copy content
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-block">
                <Button disabled>
                  <Copy className="h-4 w-4" /> Copy content
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {posted
                ? "Already posted — edits won't be saved."
                : 'This variant scored low. Review the notes above, then copy.'}
            </TooltipContent>
          </Tooltip>
        )}

        <Button variant="outline" onClick={onMarkPosted} disabled={posted}>
          <Send className="h-4 w-4" /> Mark as posted
        </Button>

        <Button
          variant="outline"
          onClick={onRegenerate}
          disabled={regenPending}
        >
          {regenPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Regenerating…
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" /> Regen this variant
            </>
          )}
        </Button>

        {variant.autopostable && !posted ? (
          <Button variant="default" onClick={onPublish}>
            <Send className="h-4 w-4" /> Publish
          </Button>
        ) : null}

      </div>
    </div>
  );
}

function MetaChip({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px]">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </span>
  );
}

function HookPillOption({
  letter,
  text,
  active,
  onSelect,
}: {
  letter: 'a' | 'b';
  text: string;
  active: boolean;
  onSelect: () => void;
}): JSX.Element {
  const preview = text.length > 60 ? `${text.slice(0, 57)}…` : text;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2 rounded-[6px] px-2.5 py-1.5 text-left text-xs transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm ring-1 ring-primary/40'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <span
        className={cn(
          'flex h-4 w-4 flex-none items-center justify-center rounded-full border text-[9px] font-semibold',
          active
            ? 'border-primary/60 bg-primary/15 text-primary'
            : 'border-border/60 bg-muted text-muted-foreground',
        )}
      >
        {letter.toUpperCase()}
      </span>
      <span className="min-w-0 truncate">{preview || 'Empty hook'}</span>
    </button>
  );
}

/**
 * Detect the "regeneration failed and we inserted a fallback stub" state.
 * The workflow's critique-variants activity writes a note with this exact
 * message when the LLM call errors; treating any warn-severity note carrying
 * that shape as "auto-scoring failed" lets us surface an inline Retry button
 * without needing a dedicated backend flag.
 */
function isRegenFallback(notes: CritiqueNote[]): boolean {
  return notes.some(
    (n) =>
      n.severity === 'warn' &&
      typeof n.message === 'string' &&
      /could not automatically score|critique step failed/i.test(n.message),
  );
}

function CritiqueNotesRow({
  notes,
  isRegenFallback: fallback = false,
  onRetry,
  retrying = false,
}: {
  notes: CritiqueNote[];
  isRegenFallback?: boolean;
  onRetry?: () => void;
  retrying?: boolean;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? notes : notes.slice(0, 1);
  const extra = notes.length - shown.length;
  const dot = (severity: CritiqueNote['severity']) =>
    severity === 'block'
      ? 'bg-destructive'
      : severity === 'warn'
        ? 'bg-warning'
        : 'bg-info';
  return (
    <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2">
      <div className="flex items-start gap-2">
        <AlertOctagon className="mt-0.5 h-3.5 w-3.5 flex-none text-warning" />
        <div className="flex-1 space-y-1 text-xs">
          {shown.map((n, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={cn('mt-1 h-1.5 w-1.5 flex-none rounded-full', dot(n.severity))} />
              <span className="text-foreground">{n.message}</span>
            </div>
          ))}
          {extra > 0 || expanded ? (
            <button
              type="button"
              className="text-[11px] font-medium text-primary hover:underline"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? 'Show less' : `Show ${extra} more`}
            </button>
          ) : null}
        </div>
        {fallback && onRetry ? (
          <Button
            size="sm"
            variant="outline"
            onClick={onRetry}
            disabled={retrying}
            className="flex-none"
          >
            {retrying ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Retrying…
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5" /> Try again
              </>
            )}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/* ─────────────────────────── Prompt tab ─────────────────────────── */

function PromptTab({
  variant,
  onCopyPrompt,
}: {
  variant: OffPageBriefVariant;
  onCopyPrompt: () => void;
}): JSX.Element {
  return (
    <div className="flex h-full flex-col gap-3">
      {/* One-line header + primary action side-by-side, no big callout. */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Paste this into ChatGPT, Claude, or any AI writer to iterate on the
          variant yourself.
        </p>
        <Button variant="outline" onClick={onCopyPrompt} className="flex-none">
          <Copy className="h-4 w-4" /> Copy prompt
        </Button>
      </div>
      <Card className="min-h-0 flex-1 p-4">
        <pre className="h-full overflow-auto whitespace-pre-wrap font-mono text-xs leading-relaxed text-foreground">
          {variant.generation_prompt}
        </pre>
      </Card>
    </div>
  );
}

/* ─────────────────────────── Follow-up comment card ─────────────────────────── */

function FollowUpCommentCard({
  canonicalUrl,
}: {
  canonicalUrl: string;
}): JSX.Element {
  const commentText = `Wrote a fuller version at ${canonicalUrl} if you want the details.`;
  return (
    <div className="flex items-center gap-2 rounded-md border border-info/40 bg-info/5 px-3 py-2 text-xs">
      <Info className="h-3.5 w-3.5 flex-none text-info" />
      <span className="min-w-0 flex-1 truncate text-muted-foreground">
        After posting, add this as a reply comment on your own post:{' '}
        <span className="text-foreground">{commentText}</span>
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          void navigator.clipboard.writeText(commentText).then(
            () => toast.success('Follow-up comment copied'),
            () => toast.error('Copy failed'),
          );
        }}
      >
        <Copy className="h-3.5 w-3.5" /> Copy
      </Button>
    </div>
  );
}

/* ─────────────────────────── Generation-in-progress card ─────────────────────────── */

// Ordered stages of the workflow — surfaced to the tenant as a "Step N of 4"
// indicator so they know how far along their brief is without needing to
// understand what each stage does internally.
const STAGES = ['queued', 'planning', 'critiquing', 'grounding'] as const;
type Stage = (typeof STAGES)[number];

const IN_PROGRESS_COPY: Record<Stage, { title: string; body: string }> = {
  queued: {
    title: 'Waiting to start',
    body: "You're in line — we'll pick this up in a moment.",
  },
  planning: {
    title: 'Writing your variants',
    body: 'Drafting posts for each platform based on your brief.',
  },
  critiquing: {
    title: 'Reviewing quality',
    body: 'Checking each variant for tone, authenticity, and posting risks.',
  },
  grounding: {
    title: 'Picking the best places to post',
    body: 'Finding matching subreddits, threads, and hashtags.',
  },
};

/**
 * A run that hasn't written progress in this long has almost certainly died.
 * The server reconciles against Temporal on read (see reconcileDeadRun), but
 * until that lands we must not keep promising "1-3 minutes" — a workflow
 * cancelled at 17:15 was still showing a spinner at 18:09.
 */
const STALLED_AFTER_MS = 12 * 60 * 1000;

export function OffPageBriefInProgress({
  status,
  updatedAt,
  onCancel,
  cancelling,
}: {
  status: Stage;
  /** `updated_at` of the OPB row — used to detect a run that stopped writing. */
  updatedAt?: string;
  /** Fires POST /v1/off-page-briefs/:id/cancel. Optional so this card also
   *  renders in read-only contexts (e.g. dashboard status previews). */
  onCancel?: () => void;
  cancelling?: boolean;
}): JSX.Element {
  const copy = IN_PROGRESS_COPY[status];
  const stalled =
    updatedAt !== undefined &&
    Date.now() - new Date(updatedAt).getTime() > STALLED_AFTER_MS;

  if (stalled) {
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-5">
        <div className="flex items-start gap-3">
          <AlertOctagon className="mt-0.5 h-5 w-5 flex-none text-amber-500" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-foreground">
              This run seems to have stopped
            </div>
            <p className="mt-0.5 text-sm text-muted-foreground">
              It hasn&apos;t made progress in a while, so it probably won&apos;t
              finish on its own. Regenerate to start it again — nothing you
              already have is lost.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Last activity {relTime(updatedAt!)}.
            </p>
          </div>
          {onCancel ? (
            <Button variant="outline" size="sm" onClick={onCancel} disabled={cancelling}>
              {cancelling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Stop this run
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
  // Step numbers are 1-indexed for display; `queued` counts as step 1 so the
  // tenant sees progress start at "1 of 4" rather than "0 of 4".
  const stepIndex = STAGES.indexOf(status);
  const currentStep = stepIndex + 1;
  const totalSteps = STAGES.length;
  const percent = Math.round((currentStep / totalSteps) * 100);

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-5">
      <div className="flex items-start gap-3">
        <Loader2 className="mt-0.5 h-5 w-5 flex-none animate-spin text-primary" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">{copy.title}</span>
            <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">
              Step {currentStep} of {totalSteps}
            </span>
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">{copy.body}</p>
          <p className="mt-2 text-[11px] text-muted-foreground/80">
            Usually takes 1–3 minutes. This page updates on its own — no need to refresh.
          </p>
        </div>
        {onCancel ? (
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            disabled={cancelling}
            className="flex-none"
          >
            {cancelling ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Cancelling…
              </>
            ) : (
              <>
                <XIcon className="h-4 w-4" /> Cancel
              </>
            )}
          </Button>
        ) : null}
      </div>
      {/* Progress bar: filled portion for completed stages, animated pulse on
          the current stage so the tenant sees "we're actively on this step". */}
      <div
        className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-primary/10"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${currentStep} of ${totalSteps} steps complete`}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────── Failed-brief retry card ─────────────────────────── */

export function OffPageBriefFailed({
  detail,
  onRetry,
  retrying,
}: {
  detail: OffPageBriefDetail;
  onRetry: () => void;
  retrying: boolean;
}): JSX.Element {
  return (
    <Callout
      tone="danger"
      icon={AlertOctagon}
      title="We couldn't finish this off-page brief"
      action={
        <Button size="sm" onClick={onRetry} disabled={retrying}>
          {retrying ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Retrying…
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" /> Retry
            </>
          )}
        </Button>
      }
    >
      {detail.failure_reason ||
        'Something went wrong while generating your off-page brief. Try again in a moment.'}
    </Callout>
  );
}

/* ─────────────────────────── Skeleton (whole workspace loading) ─────────────────────────── */

export function OffPageBriefWorkspaceSkeleton(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
      <Surface tone="muted" className="p-2">
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2 px-2 pt-2">
              <div className="h-3 w-24 rounded bg-muted" />
              <div className="h-14 rounded-md bg-muted/70" />
              <div className="h-14 rounded-md bg-muted/70" />
            </div>
          ))}
        </div>
      </Surface>
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-border/60 pb-2">
          <div className="h-4 w-40 rounded bg-muted" />
          <div className="h-7 w-32 rounded bg-muted" />
        </div>
        <div className="h-6 w-56 rounded bg-muted" />
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="h-16 rounded-md bg-muted/70" />
          <div className="h-16 rounded-md bg-muted/70" />
        </div>
        <div className="h-64 rounded-md bg-muted/70" />
      </div>
    </div>
  );
}

/* ─────────────────────────── Local target/critique types re-export ─────────────────────────── */

// Re-export inline types the workspace produces so children can reuse the
// same soft shapes without re-defining them (kept module-local to avoid
// polluting @seo/types with UI concerns).
export type { VariantTarget, CritiqueNote };
